import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Stage, Layer, Rect, Line, Text, Group, Circle } from 'react-konva';
import { API_BASE } from './api';
import { theme } from './theme';

const DEFAULT_LAYOUT = {
  version: 2,
  stage: { width: 1400, height: 800 },
  elements: [],
};

const FLOORS = [
  { id: 0, label: '0 Basis' },
  { id: 1, label: '1 Empore' },
  { id: 2, label: '2 Dachgeschoss' },
];

const ROTATION_SNAP_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
const ROTATION_SNAP_TOLERANCE = 4;

const RECT_PATTERN_OPTIONS = [
  { value: 'none', label: 'Kein Muster' },
  { value: 'diagonal', label: 'Gestreift diagonal' },
  { value: 'vertical', label: 'Gestreift vertikal' },
  { value: 'horizontal', label: 'Gestreift horizontal' },
  { value: 'grid', label: 'Kariert' },
  { value: 'crosshatch', label: 'Kreuzschraffur' },
  { value: 'dots', label: 'Gepunktet' },
];
const RECT_PATTERN_VALUES = new Set(RECT_PATTERN_OPTIONS.map(option => option.value));

const DIRECTION_COLORS = {
  AUSGEHEND: '#039eff',
  EINGEHEND_LEERGUT: '#ff9800',
  EINGEHEND_RETOURE: '#ff5555',
};

const DIRECTION_LABELS = {
  AUSGEHEND: 'Ausgehend',
  EINGEHEND_LEERGUT: 'Leergut',
  EINGEHEND_RETOURE: 'Retoure',
};

const FREE_COLOR = '#9ca3af';
const SLOT_REFRESH_MS = 5000;
const CLOCK_REFRESH_MS = 1000;
const VIEWPORT_PADDING = 2;
const PREVIEW_WIDTH = 340;
const PREVIEW_HEIGHT = 210;
const PREVIEW_OFFSET = 8;
const PREVIEW_MARGIN = 8;

function normalizeLayout(value) {
  const source = value && typeof value === 'object' ? value : DEFAULT_LAYOUT;
  const stage = source.stage && typeof source.stage === 'object' ? source.stage : DEFAULT_LAYOUT.stage;
  return {
    version: 2,
    stage: {
      width: Number(stage.width) || DEFAULT_LAYOUT.stage.width,
      height: Number(stage.height) || DEFAULT_LAYOUT.stage.height,
    },
    elements: Array.isArray(source.elements)
      ? source.elements
        .filter(element => element && typeof element === 'object' && element.id && element.type)
        .map(element => ({ ...element, floor: normalizeFloor(element.floor), allFloors: element.allFloors === true, rotation: normalizeRotation(element.rotation) }))
      : [],
  };
}

function normalizeFloor(value) {
  const floor = Number(value);
  return [0, 1, 2].includes(floor) ? floor : 0;
}

function isElementVisibleOnFloor(element, floor) {
  return element?.allFloors === true || normalizeFloor(element?.floor) === normalizeFloor(floor);
}

function getRectText(element) {
  return String(element.text || '');
}

function getCircularAngleDistance(a, b) {
  const diff = Math.abs(a - b) % 360;
  return Math.min(diff, 360 - diff);
}

function normalizeRotation(value, options = {}) {
  const snap = options.snap !== false;
  let angle = Number(value);
  if (!Number.isFinite(angle)) angle = 0;
  angle = ((angle % 360) + 360) % 360;

  if (snap) {
    const snapAngle = ROTATION_SNAP_ANGLES.find(candidate => (
      getCircularAngleDistance(angle, candidate) <= ROTATION_SNAP_TOLERANCE
    ));
    if (snapAngle != null) return snapAngle;
  }

  const rounded = Math.round(angle * 1000) / 1000;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeRectPattern(value) {
  return RECT_PATTERN_VALUES.has(value) ? value : 'none';
}

function getClippedDiagonalPatternPoints(width, height, constant, direction = 1) {
  const candidates = direction === 1
    ? [
        { x: 0, y: constant },
        { x: width, y: constant - width },
        { x: constant, y: 0 },
        { x: constant - height, y: height },
      ]
    : [
        { x: 0, y: constant },
        { x: width, y: width + constant },
        { x: -constant, y: 0 },
        { x: height - constant, y: height },
      ];

  const points = [];
  candidates.forEach(point => {
    if (point.x < -0.001 || point.x > width + 0.001 || point.y < -0.001 || point.y > height + 0.001) return;
    const next = {
      x: clamp(point.x, 0, width),
      y: clamp(point.y, 0, height),
    };
    if (!points.some(existing => Math.abs(existing.x - next.x) < 0.001 && Math.abs(existing.y - next.y) < 0.001)) {
      points.push(next);
    }
  });

  if (points.length < 2) return null;
  return [points[0].x, points[0].y, points[1].x, points[1].y];
}

function getRectPatternElement(element, width, height) {
  const pattern = normalizeRectPattern(element.pattern);
  if (pattern === 'none') return null;

  const color = element.patternColor || '#94a3b8';
  const spacing = clamp(Number(element.patternSpacing) || 18, 6, 80);
  const strokeWidth = clamp(Number(element.patternStrokeWidth) || 1, 1, 8);
  const patternNodes = [];

  const addVerticalLines = () => {
    for (let x = spacing; x < width; x += spacing) {
      patternNodes.push(<Line key={`v-${x}`} points={[x, 0, x, height]} stroke={color} strokeWidth={strokeWidth} listening={false} />);
    }
  };

  const addHorizontalLines = () => {
    for (let y = spacing; y < height; y += spacing) {
      patternNodes.push(<Line key={`h-${y}`} points={[0, y, width, y]} stroke={color} strokeWidth={strokeWidth} listening={false} />);
    }
  };

  const addDiagonalLines = (direction = 1) => {
    const start = direction === 1 ? 0 : -width;
    const end = direction === 1 ? width + height : height;
    for (let constant = start; constant <= end; constant += spacing) {
      const points = getClippedDiagonalPatternPoints(width, height, constant, direction);
      if (points) {
        patternNodes.push(<Line key={`d-${direction}-${constant}`} points={points} stroke={color} strokeWidth={strokeWidth} listening={false} />);
      }
    }
  };

  if (pattern === 'vertical') addVerticalLines();
  if (pattern === 'horizontal') addHorizontalLines();
  if (pattern === 'grid') {
    addVerticalLines();
    addHorizontalLines();
  }
  if (pattern === 'diagonal') addDiagonalLines(1);
  if (pattern === 'crosshatch') {
    addDiagonalLines(1);
    addDiagonalLines(-1);
  }
  if (pattern === 'dots') {
    const radius = clamp(spacing * 0.14, 1.5, 4);
    for (let y = spacing / 2; y < height; y += spacing) {
      for (let x = spacing / 2; x < width; x += spacing) {
        patternNodes.push(<Circle key={`dot-${x}-${y}`} x={x} y={y} radius={radius} fill={color} listening={false} />);
      }
    }
  }

  return (
    <Group clipX={0} clipY={0} clipWidth={width} clipHeight={height} listening={false}>
      {patternNodes}
    </Group>
  );
}

function shouldKeepTextUpright(element, text) {
  const width = Number(element.width) || 0;
  const height = Number(element.height) || 0;
  const fontSize = Math.max(12, Number(element.fontSize) || Math.min(20, height * 0.34));
  const estimatedWidth = String(text || '').length * fontSize * 0.58;
  return width >= 70 && height >= fontSize * 1.7 && estimatedWidth <= Math.max(40, width - 12);
}

function getLocalDateInputValue(date = new Date()) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
}

function dedupeSlots(slots) {
  const byId = new Map();
  for (const slot of slots) {
    if (slot?.id == null) continue;
    byId.set(slot.id, slot);
  }
  return Array.from(byId.values()).sort((a, b) => a.start_time - b.start_time);
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getSlotJobs(slot) {
  const customJobName = String(slot?.custom_job_name || '').trim();
  const rentmanJobs = Array.isArray(slot?.jobs)
    ? slot.jobs.filter(job => job.job_id).map(job => job.job_name || `Job #${job.job_id}`)
    : [];
  return [
    ...(customJobName ? [customJobName] : []),
    ...rentmanJobs,
  ];
}

function getElementResourceType(element) {
  return element.type === 'loadingBay' ? 'loadingBay' : 'gate';
}

function getElementResourceId(element) {
  const rawValue = element.type === 'loadingBay' ? element.loadingBayId : element.gateId;
  const value = Number(rawValue);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function getElementDisplayPath(element) {
  const resourceType = getElementResourceType(element);
  const resourceId = getElementResourceId(element);
  if (!resourceId) return '';
  return resourceType === 'loadingBay' ? `/display/loadingbay/${resourceId}` : `/display/gate/${resourceId}`;
}

function getElementState(element, slots, now) {
  const resourceType = getElementResourceType(element);
  const resourceId = getElementResourceId(element);
  const nowMs = now.getTime();
  const elementSlots = slots
    .filter(slot => slot.resource_type === resourceType && Number(slot.resource_id) === Number(resourceId))
    .sort((a, b) => a.start_time - b.start_time);
  const active = elementSlots.find(slot => slot.start_time * 1000 <= nowMs && slot.end_time * 1000 > nowMs);

  if (!active) {
    return { color: FREE_COLOR, status: 'Frei', jobName: '' };
  }

  const jobs = getSlotJobs(active);
  const vehicleInfo = [
    active.license_plate,
    active.vehicle_class_name ? `(${active.vehicle_class_name})` : '',
  ].filter(Boolean).join(' ');

  return {
    slot: active,
    color: DIRECTION_COLORS[active.direction] || '#6b7280',
    status: DIRECTION_LABELS[active.direction] || 'Belegt',
    jobName: jobs.length > 0 ? jobs.join(', ') : '',
    vehicleInfo,
  };
}

function getReadableTextColor(color) {
  return color === DIRECTION_COLORS.EINGEHEND_LEERGUT || color === FREE_COLOR ? '#111827' : '#fff';
}

function getPreviewPosition(clientX, clientY) {
  const maxX = Math.max(PREVIEW_MARGIN, window.innerWidth - PREVIEW_WIDTH - PREVIEW_MARGIN);
  const maxY = Math.max(PREVIEW_MARGIN, window.innerHeight - PREVIEW_HEIGHT - PREVIEW_MARGIN);
  const preferredX = clientX + PREVIEW_OFFSET;
  const preferredY = clientY + PREVIEW_OFFSET;

  return {
    x: Math.max(PREVIEW_MARGIN, Math.min(maxX, preferredX)),
    y: Math.max(PREVIEW_MARGIN, Math.min(maxY, preferredY)),
  };
}

function getRotatedPoint(x, y, originX, originY, degrees) {
  const radians = (degrees || 0) * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: originX + x * cos - y * sin,
    y: originY + x * sin + y * cos,
  };
}

function getElementLabelPosition(element) {
  const width = element.width || 120;
  const height = element.height || 56;
  const offset = Math.max(54, height * 1.18);
  return getRotatedPoint(width / 2, -offset, element.x || 0, element.y || 0, element.rotation || 0);
}

function useElementSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const updateSize = () => {
      setSize({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    };

    updateSize();
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateSize)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(element);
    } else {
      window.addEventListener('resize', updateSize);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateSize);
      }
    };
  }, [ref]);

  return size;
}

const styles = {
  card: { ...theme.glassCard, padding: '18px', color: 'var(--board-text-strong, #fff)', overflow: 'hidden' },
  fullScreen: { width: '100vw', height: '100vh', padding: 0, margin: 0, overflow: 'hidden', background: 'var(--board-surface-strong, #05050b)', color: 'var(--board-text-strong, #fff)' },
  title: { margin: '0 0 14px', color: 'var(--board-text-strong, #fff)', fontSize: '20px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' },
  floorTabs: { display: 'inline-grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' },
  floorButton: { ...theme.button, ...theme.mutedButton, borderRadius: '6px', boxShadow: 'none', padding: '9px 12px', width: 'auto' },
  floorButtonActive: { ...theme.button, ...theme.primaryButton, borderRadius: '6px', padding: '9px 12px', width: 'auto' },
  floorTabsFullScreen: { position: 'fixed', top: '12px', right: '12px', zIndex: 10, display: 'inline-grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' },
  viewerFrame: { width: '100%', minHeight: '360px', height: 'min(68vh, 760px)', display: 'grid', placeItems: 'center', overflow: 'hidden' },
  viewerFrameFullScreen: { width: '100vw', height: '100vh', display: 'grid', placeItems: 'center', overflow: 'hidden' },
  stageShell: { display: 'inline-block', background: 'var(--board-surface-soft, #0b1020)', border: '1px solid var(--board-border, rgba(255,255,255,0.18))', boxSizing: 'border-box' },
  stageShellFullScreen: { display: 'inline-block', background: 'var(--board-surface-soft, #0b1020)', boxSizing: 'border-box' },
  preview: {
    position: 'fixed',
    width: PREVIEW_WIDTH,
    height: PREVIEW_HEIGHT,
    zIndex: 1000,
    pointerEvents: 'none',
    background: 'var(--board-surface-strong, #05050b)',
    border: '1px solid var(--board-border-strong, rgba(255,255,255,0.28))',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 18px 48px rgba(0,0,0,0.46)',
  },
  previewFrame: {
    width: '100%',
    height: '100%',
    border: 0,
    pointerEvents: 'none',
    background: 'var(--board-surface-strong, #05050b)',
  },
  empty: { color: 'var(--board-text-muted, rgba(255,255,255,0.62))', fontSize: '14px' },
};

function FloorSelector({ activeFloor, onChange, fullScreen }) {
  return (
    <div style={fullScreen ? styles.floorTabsFullScreen : styles.floorTabs}>
      {FLOORS.map(floor => (
        <button
          key={floor.id}
          style={activeFloor === floor.id ? styles.floorButtonActive : styles.floorButton}
          type="button"
          onClick={() => onChange(floor.id)}
        >
          {floor.label}
        </button>
      ))}
    </div>
  );
}

function LayoutViewer({ title = 'Display-Uebersicht', fullScreen = false }) {
  const frameRef = useRef(null);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [slots, setSlots] = useState([]);
  const [now, setNow] = useState(new Date());
  const [loaded, setLoaded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [activeFloor, setActiveFloor] = useState(0);
  const frameSize = useElementSize(frameRef);

  useEffect(() => {
    fetch(`${API_BASE}/public/warehouse-layout`)
      .then(res => res.json())
      .then(data => {
        setLayout(normalizeLayout(data.layout));
        setLoaded(true);
      })
      .catch(() => {
        setLayout(DEFAULT_LAYOUT);
        setLoaded(true);
      });
  }, []);

  useEffect(() => {
    const loadSlots = () => {
      const yesterday = getLocalDateInputValue(addDays(new Date(), -1));
      const today = getLocalDateInputValue();
      const tomorrow = getLocalDateInputValue(addDays(new Date(), 1));

      Promise.all([
        fetch(`${API_BASE}/public/slots?date=${yesterday}`).then(res => res.json()),
        fetch(`${API_BASE}/public/slots?date=${today}`).then(res => res.json()),
        fetch(`${API_BASE}/public/slots?date=${tomorrow}`).then(res => res.json()),
      ])
        .then(([yesterdaySlots, todaySlots, tomorrowSlots]) => {
          setSlots(dedupeSlots([
            ...(Array.isArray(yesterdaySlots) ? yesterdaySlots : []),
            ...(Array.isArray(todaySlots) ? todaySlots : []),
            ...(Array.isArray(tomorrowSlots) ? tomorrowSlots : []),
          ]));
        })
        .catch(() => setSlots([]));
    };

    loadSlots();
    const interval = window.setInterval(loadSlots, SLOT_REFRESH_MS);
    window.addEventListener('focus', loadSlots);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', loadSlots);
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), CLOCK_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, []);

  const visibleElements = useMemo(
    () => layout.elements.filter(element => isElementVisibleOnFloor(element, activeFloor)),
    [activeFloor, layout.elements]
  );

  const labels = useMemo(() => (
    visibleElements
      .filter(element => element.type === 'gate' || element.type === 'loadingBay')
      .map(element => {
        const state = getElementState(element, slots, now);
        const position = getElementLabelPosition(element);
        return { element, state, position };
      })
  ), [now, slots, visibleElements]);

  const scale = useMemo(() => {
    if (!frameSize.width || !frameSize.height) return 1;
    return Math.min(
      1,
      Math.max(0.1, (frameSize.width - VIEWPORT_PADDING) / layout.stage.width),
      Math.max(0.1, (frameSize.height - VIEWPORT_PADDING) / layout.stage.height)
    );
  }, [frameSize.height, frameSize.width, layout.stage.height, layout.stage.width]);

  const showPreview = (element, event) => {
    const displayPath = getElementDisplayPath(element);
    if (!displayPath) return;
    const stage = event.target.getStage();
    if (stage) stage.container().style.cursor = 'pointer';
    const position = getPreviewPosition(event.evt.clientX, event.evt.clientY);
    setPreview({ displayPath, title: element.label || 'Display', ...position });
  };

  const movePreview = (element, event) => {
    const displayPath = getElementDisplayPath(element);
    if (!displayPath) return;
    const position = getPreviewPosition(event.evt.clientX, event.evt.clientY);
    setPreview({ displayPath, title: element.label || 'Display', ...position });
  };

  const hidePreview = () => {
    const stage = frameRef.current?.querySelector('canvas')?.closest('.konvajs-content');
    if (stage) stage.style.cursor = 'default';
    setPreview(null);
  };

  const previewPortal = preview && createPortal(
    <div
      style={{
        ...styles.preview,
        left: `${preview.x}px`,
        top: `${preview.y}px`,
      }}
    >
      <iframe
        title={preview.title}
        src={preview.displayPath}
        style={styles.previewFrame}
      />
    </div>,
    document.body
  );

  const renderElement = element => {
    if (element.type === 'gate' || element.type === 'loadingBay') {
      const state = getElementState(element, slots, now);
      const width = element.width || 120;
      const height = element.height || 56;
      const displayPath = getElementDisplayPath(element);
      const defaultColor = element.type === 'loadingBay' ? '#f97316' : '#039eff';
      const defaultLabel = element.type === 'loadingBay' ? 'Ladezone' : 'Tor';
      const label = element.label || defaultLabel;
      const keepUpright = shouldKeepTextUpright(element, label);
      return (
        <Group
          key={element.id}
          x={element.x || 0}
          y={element.y || 0}
          rotation={element.rotation || 0}
          onMouseEnter={displayPath ? e => showPreview(element, e) : undefined}
          onMouseMove={displayPath ? e => movePreview(element, e) : undefined}
          onMouseLeave={displayPath ? hidePreview : undefined}
        >
          <Rect
            width={width}
            height={height}
            fill={state.color || element.color || defaultColor}
            stroke="rgba(255,255,255,0.72)"
            strokeWidth={2}
            cornerRadius={6}
          />
          <Text
            x={width / 2}
            y={height / 2}
            offsetX={width / 2}
            offsetY={height / 2}
            rotation={keepUpright ? -(element.rotation || 0) : 0}
            text={label}
            width={width}
            height={height}
            align="center"
            verticalAlign="middle"
            fill={getReadableTextColor(state.color || element.color || defaultColor)}
            fontStyle="bold"
            fontSize={Math.max(12, Math.min(20, height * 0.34))}
            listening={false}
          />
        </Group>
      );
    }

    if (element.type === 'rect') {
      const width = element.width || 180;
      const height = element.height || 90;
      const text = getRectText(element);
      const keepUpright = text && shouldKeepTextUpright(element, text);
      return (
        <Group
          key={element.id}
          x={element.x || 0}
          y={element.y || 0}
          rotation={element.rotation || 0}
        >
          <Rect
            width={width}
            height={height}
            fill={element.color || '#334155'}
            stroke="rgba(255,255,255,0.32)"
            strokeWidth={2}
            cornerRadius={4}
          />
          {getRectPatternElement(element, width, height)}
          {text && (
            <Text
              x={width / 2}
              y={height / 2}
              offsetX={width / 2}
              offsetY={height / 2}
              rotation={keepUpright ? -(element.rotation || 0) : 0}
              text={text}
              width={width}
              height={height}
              align="center"
              verticalAlign="middle"
              fill={element.textColor || '#fff'}
              fontSize={Math.max(8, element.fontSize || 18)}
              fontStyle="bold"
              wrap="word"
              listening={false}
            />
          )}
        </Group>
      );
    }

    if (element.type === 'line') {
      return (
        <Line
          key={element.id}
          x={element.x || 0}
          y={element.y || 0}
          rotation={element.rotation || 0}
          points={element.points || [0, 0, 180, 0]}
          stroke={element.stroke || '#f8fafc'}
          strokeWidth={3}
          lineCap="round"
          lineJoin="round"
        />
      );
    }

    return (
      <Text
        key={element.id}
        x={element.x || 0}
        y={element.y || 0}
        rotation={element.rotation || 0}
        text={element.text || element.label || 'Text'}
        width={element.width || 220}
        height={element.height || 60}
        fill={element.color || '#f8fafc'}
        fontSize={element.fontSize || 28}
        fontStyle="bold"
        wrap="word"
      />
    );
  };

  return (
    <div style={fullScreen ? styles.fullScreen : styles.card}>
      {fullScreen ? (
        <FloorSelector activeFloor={activeFloor} onChange={setActiveFloor} fullScreen />
      ) : (
        <div style={styles.header}>
          <h2 style={{ ...styles.title, margin: 0 }}>{title}</h2>
          <FloorSelector activeFloor={activeFloor} onChange={setActiveFloor} />
        </div>
      )}
      {loaded && layout.elements.length === 0 ? (
        <p style={styles.empty}>Noch kein Lagerlayout gespeichert.</p>
      ) : (
        <div ref={frameRef} style={fullScreen ? styles.viewerFrameFullScreen : styles.viewerFrame}>
          <div style={fullScreen ? styles.stageShellFullScreen : styles.stageShell}>
            <Stage
              width={layout.stage.width * scale}
              height={layout.stage.height * scale}
              scaleX={scale}
              scaleY={scale}
            >
              <Layer>
                <Rect width={layout.stage.width} height={layout.stage.height} fill="#0b1020" />
                {visibleElements.map(renderElement)}
                {labels.map(({ element, state, position }) => (
                  <Text
                    key={`${element.id}-status-label`}
                    x={position.x}
                    y={position.y}
                    offsetX={110}
                    offsetY={18}
                    width={220}
                    align="center"
                    text={`${state.status}${state.jobName ? `\n${state.jobName}` : ''}${state.vehicleInfo ? `\n${state.vehicleInfo}` : ''}`}
                    fill="#fff"
                    fontSize={17}
                    fontStyle="bold"
                    lineHeight={1.14}
                    shadowColor="rgba(0,0,0,0.82)"
                    shadowBlur={8}
                    shadowOffsetY={2}
                    listening={false}
                  />
                ))}
              </Layer>
            </Stage>
          </div>
        </div>
      )}
      {previewPortal}
    </div>
  );
}

export default LayoutViewer;
